import styles from './FormCheckboxGroup.module.css';

/**
 * FormCheckboxGroup — vertically-stacked checkbox options.
 *
 * Multi-select: emits an array of selected values to onChange.
 * Checkbox size follows the canonical Henaket spec (40×40 standard,
 * 2px near-black border) since 0.36.362. This component adds the
 * SIZE VARIANT layer on top:
 *   size='standard' (default) → 40×40 checkbox, 16px gap, no row padding
 *   size='small'              → 24×24 checkbox, 8px gap, 8px vertical
 *                                row padding so the row still occupies
 *                                the standard 40px element height
 *
 * Props:
 *   value         — array of currently-selected option values
 *   onChange(arr) — called with the new array when user toggles one
 *   options       — Array<{value, label}>
 *   name          — base name (each checkbox gets name="${name}[]"
 *                   conceptually; in practice value lives in state)
 *   disabled      — disables the group (for canvas mock-up)
 *   ariaInvalid   — sets aria-invalid on each input
 *   size          — 'standard' | 'small' (default 'standard')
 */
export default function FormCheckboxGroup({
  value = [],
  onChange,
  options = [],
  name,
  disabled = false,
  ariaInvalid = false,
  size = 'standard',
  // 0.44.01 - 'horizontal' puts the options on one line (wrapping when
  // the line is full), e.g. for filters above a table.
  layout = 'vertical',
  /* 0.48.39 - No top margin, as FormRadioGroup's flushTop: for a group
     on one line with other controls (a filter beside a search field),
     or right under a configuration label. */
  flushTop = false,
}) {
  const safeValue = Array.isArray(value) ? value : [];
  const isSmall = size === 'small';
  const rowClass = [styles.row, isSmall && styles.rowSmall]
    .filter(Boolean).join(' ');
  const inputClass = [styles.input, isSmall && styles.inputSmall]
    .filter(Boolean).join(' ');

  const toggle = (optValue) => {
    const has = safeValue.includes(optValue);
    const next = has
      ? safeValue.filter(v => v !== optValue)
      : [...safeValue, optValue];
    onChange?.(next);
  };

  return (
    <div className={[styles.group, layout === 'horizontal' && styles.groupHorizontal,
                     flushTop && styles.groupFlushTop].filter(Boolean).join(' ')}
         role="group">
      {options.map((opt, idx) => {
        const id = `${name || 'check'}-${idx}-${opt.value}`;
        const checked = safeValue.includes(opt.value);
        return (
          <label key={opt.value} className={rowClass} htmlFor={id}>
            <input
              id={id}
              className={inputClass}
              type="checkbox"
              name={name}
              value={opt.value}
              checked={checked}
              disabled={disabled}
              aria-invalid={ariaInvalid}
              onChange={() => toggle(opt.value)}
            />
            <span className={styles.label}>{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}
